import { Box, IconButton, Link as MuiLink } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import type { AnchorHTMLAttributes } from "react";
import { useAppDispatch } from "../../app/hooks";
import { dismissMessage } from "./messageQueueSlice";
import { useMessageQueue } from "./useMessageQueue";
import { createPortal } from "react-dom";
import remarkGfm from "remark-gfm";

const markdownContainerStyles = {
  flex: 1,
  "& p": {
    marginBottom: 1.5
  },
  "& ul": {
    listStyle: "disc",
    paddingLeft: 3,
    marginY: 1.5
  },
  "& ol": {
    listStyle: "decimal",
    paddingLeft: 3,
    marginY: 1.5
  },
  "& li": {
    marginBottom: 0.5
  },
  "& a": {
    color: "primary.main",
    fontWeight: 500
  }
};

const convertEmailLiterals = (text: string): string =>
  text.replace(
    /(?<!\()\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
    (match) => `[${match}](mailto:${match})`
  );

const normalizeMarkdown = (body: string): string => {
  const convertUnicodeBullets = body.replace(
    /([^\n]*?)[•▪‣·]\s+/g,
    (_, prefix) => `${prefix}\n- `
  );

  const ensureHyphenLists = convertUnicodeBullets.replace(
    /([^\n])(-\s+)/g,
    (_, prev, marker) => `${prev}\n\n${marker}`
  );

  const ensureNumericLists = ensureHyphenLists.replace(
    /([^\n])(\d+\.\s+)/g,
    (_, prev, marker) => `${prev}\n\n${marker}`
  );

  return convertEmailLiterals(ensureNumericLists);
};

const markdownComponents: Components = {
  a: (componentProps) => {
    const anchorProps =
      componentProps as AnchorHTMLAttributes<HTMLAnchorElement>;
    const href = anchorProps.href ?? "";
    const isMailto = href.startsWith("mailto:");
    const target = isMailto
      ? anchorProps.target
      : anchorProps.target ?? "_blank";
    const rel = isMailto ? anchorProps.rel : anchorProps.rel ?? "noreferrer";
    return (
      <MuiLink
        {...anchorProps}
        component="a"
        target={target}
        rel={rel}
        sx={{ fontWeight: 500 }}
      />
    );
  }
};

export const MessageCenter = () => {
  const dispatch = useAppDispatch();
  const { messages, hasBlocking } = useMessageQueue();

  if (messages.length === 0) return null;
  if (typeof document === "undefined") return null;

  const overlay = (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        overflowY: "auto",
        pt: 6,
        pb: 6,
        backgroundColor: (theme) =>
          hasBlocking ? theme.palette.background.default : "rgba(0,0,0,0.6)"
      }}
    >
      <Box
        sx={{
          width: "min(720px, 100%)",
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        {messages.map((msg) => {
          const isBlocking = !msg.dismissible;
          const Icon = isBlocking ? WarningAmberRoundedIcon : InfoOutlinedIcon;
          const iconColor = isBlocking ? "error.main" : "info.main";
          const normalizedBody = normalizeMarkdown(msg.body);

          return (
            <Box
              key={msg.id}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                p: 3,
                boxShadow: 6,
                position: "relative",
                opacity: isBlocking ? 1 : 0.95,
                maxWidth: hasBlocking ? "min(680px, 100%)" : undefined,
                marginX: hasBlocking ? "auto" : undefined
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ color: iconColor, pt: 0.5, flexShrink: 0 }}>
                  <Icon fontSize="large" />
                </Box>
                <Box sx={markdownContainerStyles}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {normalizedBody}
                  </ReactMarkdown>
                </Box>
              </Box>
              {msg.dismissible && (
                <IconButton
                  aria-label="Dismiss message"
                  onClick={() => dispatch(dismissMessage(msg))}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return createPortal(overlay, document.body);
};


